const SPLIT_TOTAL_TARGET = 1;
const SPLIT_PERCENT_MULTIPLIER = 100;
const SPLIT_TARGET_PERCENTAGE = SPLIT_TOTAL_TARGET * SPLIT_PERCENT_MULTIPLIER;
const SPLIT_GROUP_TARGET = SPLIT_TOTAL_TARGET / 2;

const normalizeZero = value => (Object.is(value, -0) ? 0 : value);

const roundToPrecision = (value, precision) =>
  normalizeZero(Number(value.toFixed(precision)));

const roundToTwoDecimals = value => roundToPrecision(value, 2);

const getSalesPersonSplitKey = salesPerson =>
  Object.keys(salesPerson || {}).find(key => key.startsWith("split_"));

const getSalesPersonTypeKey = salesPerson =>
  Object.keys(salesPerson || {}).find(key => key.startsWith("type_"));

const getTotalSplitRatio = (salesPeople = [], values = {}) =>
  salesPeople.reduce((total, salesPerson) => {
    const splitKey = getSalesPersonSplitKey(salesPerson);

    return total + (splitKey ? Number(values?.[splitKey] || 0) : 0);
  }, 0);

const convertSplitRatioToPercentage = splitRatio =>
  roundToTwoDecimals(Number(splitRatio || 0) * SPLIT_PERCENT_MULTIPLIER);

const getSplitAllocationSummary = (salesPeople = [], values = {}) => {
  const allocatedRatio = getTotalSplitRatio(salesPeople, values);

  return {
    allocatedRatio,
    allocatedPercentage: convertSplitRatioToPercentage(allocatedRatio),
    remainingPercentage: roundToTwoDecimals(
      (SPLIT_TOTAL_TARGET - allocatedRatio) * SPLIT_PERCENT_MULTIPLIER,
    ),
  };
};

const isSplitAllocationComplete = allocatedRatio =>
  convertSplitRatioToPercentage(allocatedRatio) === SPLIT_TARGET_PERCENTAGE;

const formatSplitPercentage = splitRatio =>
  `${convertSplitRatioToPercentage(splitRatio).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

const getEvenlyDistributedSplitRatios = (totalRatio, salesUserCount) => {
  if (!salesUserCount) {
    return [];
  }

  const baseSplitRatio = roundToPrecision(totalRatio / salesUserCount, 6);
  const initialSplitRatios = Array.from(
    { length: Math.max(salesUserCount - 1, 0) },
    () => baseSplitRatio,
  );
  const allocatedRatio = initialSplitRatios.reduce(
    (total, splitRatio) => total + splitRatio,
    0,
  );
  const finalSplitRatio = roundToPrecision(
    totalRatio - allocatedRatio,
    6,
  );

  return [...initialSplitRatios, finalSplitRatio];
};

const SELLING_GROUP = "sales";
const SOURCING_GROUP = "source";

const getSalesPersonTypeGroupFromName = name => {
  const normalizedTypeName = (name || "").trim().toLowerCase();

  if (normalizedTypeName.includes("sourc")) {
    return SOURCING_GROUP;
  }

  // "sell" covers Sell / Selling / Seller; "sale" covers Sale / Sales.
  if (
    normalizedTypeName.includes("sell") ||
    normalizedTypeName.includes("sale")
  ) {
    return SELLING_GROUP;
  }

  return "other";
};

const getSalesPersonTypeGroup = (typeId, invoiceSalesPersonTypes = []) => {
  const typeName = invoiceSalesPersonTypes.find(type => type?.id === typeId)?.name;
  return getSalesPersonTypeGroupFromName(typeName);
};

const findFirstTypeIdInGroup = (invoiceSalesPersonTypes = [], group) =>
  invoiceSalesPersonTypes.find(
    type => getSalesPersonTypeGroupFromName(type?.name) === group,
  )?.id ?? null;

const getSellingTypeId = (invoiceSalesPersonTypes = []) =>
  findFirstTypeIdInGroup(invoiceSalesPersonTypes, SELLING_GROUP);

const getSourcingTypeId = (invoiceSalesPersonTypes = []) =>
  findFirstTypeIdInGroup(invoiceSalesPersonTypes, SOURCING_GROUP);

const getSalesPersonGroupSummary = (
  salesPeople = [],
  values = {},
  invoiceSalesPersonTypes = [],
) => {
  const groupedSalesPeople = salesPeople.reduce(
    (groupedRows, salesPerson, index) => {
      const typeKey = getSalesPersonTypeKey(salesPerson);
      const typeId = typeKey ? values?.[typeKey] : null;
      const salesPersonTypeGroup = getSalesPersonTypeGroup(
        typeId,
        invoiceSalesPersonTypes,
      );

      return {
        ...groupedRows,
        [salesPersonTypeGroup]: [...groupedRows[salesPersonTypeGroup], index],
      };
    },
    { sales: [], source: [], other: [] },
  );
  const distinctTypeIds = [
    ...new Set(
      salesPeople
        .map(salesPerson => {
          const typeKey = getSalesPersonTypeKey(salesPerson);
          return typeKey ? values?.[typeKey] : null;
        })
        .filter(typeId => typeId != null),
    ),
  ];

  return {
    ...groupedSalesPeople,
    distinctTypeIds,
  };
};

const hasSingleSalesPersonTypeGroup = (
  salesPeople = [],
  values = {},
  invoiceSalesPersonTypes = [],
) => {
  const groupedSalesPeople = getSalesPersonGroupSummary(
    salesPeople,
    values,
    invoiceSalesPersonTypes,
  );
  const hasSalesRows = groupedSalesPeople.sales.length > 0;
  const hasSourceRows = groupedSalesPeople.source.length > 0;
  const hasOtherRows = groupedSalesPeople.other.length > 0;

  return !hasOtherRows && hasSalesRows !== hasSourceRows;
};

const getEqualSplitRatios = (
  salesPeople = [],
  values = {},
  invoiceSalesPersonTypes = [],
) => {
  if (!salesPeople.length) {
    return [];
  }

  const groupedSalesPeople = getSalesPersonGroupSummary(
    salesPeople,
    values,
    invoiceSalesPersonTypes,
  );
  const hasSalesRows = groupedSalesPeople.sales.length > 0;
  const hasSourceRows = groupedSalesPeople.source.length > 0;
  const hasOtherRows = groupedSalesPeople.other.length > 0;
  const { distinctTypeIds } = groupedSalesPeople;

  if (hasSalesRows && hasSourceRows) {
    const splitRatios = Array.from({ length: salesPeople.length }, () => 0);
    const salesRatios = getEvenlyDistributedSplitRatios(
      SPLIT_GROUP_TARGET,
      groupedSalesPeople.sales.length,
    );
    const sourceRatios = getEvenlyDistributedSplitRatios(
      SPLIT_GROUP_TARGET,
      groupedSalesPeople.source.length,
    );

    groupedSalesPeople.sales.forEach((salesPersonIndex, index) => {
      splitRatios[salesPersonIndex] = salesRatios[index];
    });

    groupedSalesPeople.source.forEach((salesPersonIndex, index) => {
      splitRatios[salesPersonIndex] = sourceRatios[index];
    });

    return splitRatios;
  }

  if (hasOtherRows && distinctTypeIds.length === 2) {
    const splitRatios = Array.from({ length: salesPeople.length }, () => 0);
    const firstTypeRows = salesPeople.reduce((rowIndexes, salesPerson, index) => {
      const typeKey = getSalesPersonTypeKey(salesPerson);
      const typeId = typeKey ? values?.[typeKey] : null;

      return typeId === distinctTypeIds[0] ? [...rowIndexes, index] : rowIndexes;
    }, []);
    const secondTypeRows = salesPeople.reduce((rowIndexes, salesPerson, index) => {
      const typeKey = getSalesPersonTypeKey(salesPerson);
      const typeId = typeKey ? values?.[typeKey] : null;

      return typeId === distinctTypeIds[1] ? [...rowIndexes, index] : rowIndexes;
    }, []);
    const firstTypeRatios = getEvenlyDistributedSplitRatios(
      SPLIT_GROUP_TARGET,
      firstTypeRows.length,
    );
    const secondTypeRatios = getEvenlyDistributedSplitRatios(
      SPLIT_GROUP_TARGET,
      secondTypeRows.length,
    );

    firstTypeRows.forEach((salesPersonIndex, index) => {
      splitRatios[salesPersonIndex] = firstTypeRatios[index];
    });

    secondTypeRows.forEach((salesPersonIndex, index) => {
      splitRatios[salesPersonIndex] = secondTypeRatios[index];
    });

    return splitRatios;
  }

  return getEvenlyDistributedSplitRatios(SPLIT_TOTAL_TARGET, salesPeople.length);
};

export {
  SPLIT_TOTAL_TARGET,
  SPLIT_TARGET_PERCENTAGE,
  getSalesPersonSplitKey,
  getSalesPersonTypeKey,
  getSellingTypeId,
  getSourcingTypeId,
  hasSingleSalesPersonTypeGroup,
  getTotalSplitRatio,
  convertSplitRatioToPercentage,
  getSplitAllocationSummary,
  isSplitAllocationComplete,
  formatSplitPercentage,
  getEqualSplitRatios,
};
